import { type GoogleTokenResponse, loadedOAuth2 } from './googleApis'

// drive.file opens only the files the customer picks, never the rest of the drive.
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file'
const CONSENT_REFUSED = 'access_denied'
const POPUP_CLOSED = 'popup_closed'

// The message carries Google's failure code only, never the token.
export class GoogleSignInError extends Error {
  constructor(failure: string) {
    super(`Google sign-in failed: ${failure}`)
    this.name = 'GoogleSignInError'
  }
}

type TokenOutcome = (token: string | null) => void

// A failed revoke leaves a drive.file token that Google expires within the hour; the customer has nothing to do.
const ignoreRevocationResult = () => {}

export const revokeDriveToken = (token: string) =>
  loadedOAuth2().revoke(token, ignoreRevocationResult)

// With granular consent the customer can untick Drive access and still get a token.
const settleTokenResponse = (
  response: GoogleTokenResponse,
  resolve: TokenOutcome,
  reject: (error: Error) => void,
) => {
  if (response.error === CONSENT_REFUSED) return resolve(null)
  if (response.error || !response.access_token) {
    return reject(new GoogleSignInError(response.error ?? 'no-token'))
  }
  if (loadedOAuth2().hasGrantedAllScopes(response, DRIVE_FILE_SCOPE)) {
    return resolve(response.access_token)
  }
  revokeDriveToken(response.access_token)
  resolve(null)
}

// Resolves null when the customer closes the sign-in popup or refuses consent: that picks nothing.
// It must be called inside the click, or the browser blocks the popup.
export const requestDriveToken = (clientId: string) =>
  new Promise<string | null>((resolve, reject) => {
    const tokenClient = loadedOAuth2().initTokenClient({
      client_id: clientId,
      scope: DRIVE_FILE_SCOPE,
      callback: (response) => settleTokenResponse(response, resolve, reject),
      error_callback: ({ type }) =>
        type === POPUP_CLOSED
          ? resolve(null)
          : reject(new GoogleSignInError(type)),
    })
    tokenClient.requestAccessToken()
  })
